import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CONTENT = {
  es: {
    title: "Términos y Condiciones",
    eyebrow: "Canales Franchise LLC · Plataforma de Capacitación",
    updated: "Última actualización: 30 de abril de 2026",
    intro: "Estos términos rigen el uso de la plataforma interna de capacitación de Canales Franchise LLC. Al acceder o usar la plataforma aceptas estos términos. Si no estás de acuerdo, no debes utilizarla.",
    sections: [
      {
        h: "1. Uso autorizado",
        p: "Esta plataforma es de uso exclusivo del personal autorizado de Canales Franchise LLC: vendedores, managers, administradores y miembros del equipo de dirección. No está abierta al público general. Cualquier uso por personas no autorizadas está prohibido. La cuenta es personal e intransferible — no compartas tu contraseña."
      },
      {
        h: "2. Funcionalidad de la plataforma",
        p: "La plataforma ofrece: (a) cursos en video y PDF; (b) exámenes de capacitación con calificación automática; (c) sistema de puntos y catálogo de recompensas; (d) hub de idiomas con asistente IA; (e) evaluaciones para vendedores y managers; (f) notificaciones por correo y SMS sobre actividad relevante. Canales Franchise LLC se reserva el derecho de modificar, agregar o retirar funcionalidades en cualquier momento."
      },
      {
        h: "3. Notificaciones SMS",
        p: "La plataforma envía mensajes SMS transaccionales únicamente a tres (3) números pre-autorizados pertenecientes al equipo de dirección. Los SMS se disparan en eventos operativos: inicio de sesión de empleados, finalización de cursos y metas de puntos. NO son mensajes promocionales. Al ser destinatario consientes recibir hasta ~15 mensajes por día hábil. Puedes cancelar respondiendo STOP o solicitando al administrador retirarte de la lista. Aplican las tarifas estándar de tu operador móvil."
      },
      {
        h: "4. Conducta de uso",
        p: "Te comprometes a: (a) usar la plataforma de buena fe y solo para fines de capacitación; (b) no intentar acceder a cuentas de otros usuarios; (c) no realizar ingeniería inversa, scraping masivo o copia no autorizada del contenido; (d) no subir contenido ilegal, ofensivo o que vulnere derechos de terceros; (e) reportar inmediatamente cualquier vulnerabilidad o brecha que detectes."
      },
      {
        h: "5. Propiedad intelectual",
        p: "Todo el contenido de la plataforma — cursos, videos, PDFs, materiales de capacitación, catálogo de recompensas, marca y diseños — es propiedad de Canales Franchise LLC o de sus licenciantes. Tu acceso es una licencia limitada, personal y no transferible para fines de capacitación interna. No tienes derecho a redistribuir, vender ni hacer trabajo derivado del contenido sin autorización escrita."
      },
      {
        h: "6. Puntos, recompensas y certificados",
        p: "Los puntos otorgados por completar capacitación son simbólicos y pueden canjearse por las recompensas listadas en el catálogo, sujetas a disponibilidad. Los certificados emitidos por la plataforma reconocen tu progreso interno; no constituyen credenciales académicas oficiales. Canales Franchise LLC puede ajustar el sistema de puntos o recompensas en cualquier momento, sin afectar puntos ya canjeados."
      },
      {
        h: "7. Limitación de responsabilidad",
        p: "La plataforma se ofrece \"tal cual\" y \"según disponibilidad\". Canales Franchise LLC no garantiza que el servicio sea ininterrumpido o libre de errores. Hacemos esfuerzos razonables para mantener la disponibilidad y seguridad. En la medida permitida por la ley, no asumimos responsabilidad por pérdidas indirectas, lucro cesante o daños incidentales derivados del uso o imposibilidad de uso."
      },
      {
        h: "8. Suspensión y terminación",
        p: "Canales Franchise LLC puede suspender o terminar tu acceso si: (a) infringes estos términos; (b) finalizas tu relación laboral con la empresa; (c) detectamos uso fraudulento o abusivo. Tras la terminación se conservarán solo los registros que la empresa requiera por motivos de cumplimiento."
      },
      {
        h: "9. Modificaciones a los términos",
        p: "Podemos actualizar estos términos periódicamente. Los cambios entran en vigor al publicarse. Si los cambios son sustanciales, te notificaremos por correo o dentro de la plataforma. El uso continuado después de la actualización implica aceptación."
      },
      {
        h: "10. Ley aplicable y contacto",
        p: "Estos términos se rigen por las leyes de los Estados Unidos de América. Cualquier controversia se someterá a los tribunales competentes correspondientes a la sede de Canales Franchise LLC en los Estados Unidos. Para preguntas escribe a: antonio@canalesfranchise.com."
      },
    ],
  },
  en: {
    title: "Terms & Conditions",
    eyebrow: "Canales Franchise LLC · Training Platform",
    updated: "Last updated: April 30, 2026",
    intro: "These terms govern the use of Canales Franchise LLC' internal training platform. By accessing or using the platform you accept these terms. If you do not agree, you must not use the platform.",
    sections: [
      {
        h: "1. Authorized use",
        p: "This platform is for exclusive use by authorized Canales Franchise LLC personnel: salespeople, managers, administrators and leadership team members. It is not open to the general public. Any use by unauthorized persons is forbidden. The account is personal and non-transferable — do not share your password."
      },
      {
        h: "2. Platform functionality",
        p: "The platform offers: (a) video and PDF courses; (b) training exams with automatic grading; (c) points system and rewards catalog; (d) language hub with AI assistant; (e) evaluations for salespeople and managers; (f) email and SMS notifications about relevant activity. Canales Franchise LLC reserves the right to modify, add or remove features at any time."
      },
      {
        h: "3. SMS notifications",
        p: "The platform sends transactional SMS messages exclusively to three (3) pre-authorized phone numbers belonging to the leadership team. SMS are triggered by operational events: employee logins, course completions and points milestones. They are NOT promotional messages. By being a recipient you consent to receiving up to ~15 messages per business day. You may opt out by replying STOP or asking the administrator to remove you from the list. Standard mobile carrier rates apply."
      },
      {
        h: "4. User conduct",
        p: "You commit to: (a) use the platform in good faith and exclusively for training purposes; (b) not attempt to access other users' accounts; (c) not perform reverse engineering, mass scraping or unauthorized copying of content; (d) not upload illegal, offensive or rights-infringing content; (e) immediately report any vulnerability or breach you detect."
      },
      {
        h: "5. Intellectual property",
        p: "All platform content — courses, videos, PDFs, training materials, rewards catalog, brand and designs — is owned by Canales Franchise LLC or its licensors. Your access is a limited, personal, non-transferable license for internal training purposes. You have no right to redistribute, resell or create derivative works from the content without written authorization."
      },
      {
        h: "6. Points, rewards and certificates",
        p: "Points awarded for completing training are symbolic and may be redeemed for rewards listed in the catalog, subject to availability. Certificates issued by the platform recognize your internal progress; they do not constitute official academic credentials. Canales Franchise LLC may adjust the points or rewards system at any time, without affecting already-redeemed points."
      },
      {
        h: "7. Limitation of liability",
        p: "The platform is provided \"as is\" and \"as available\". Canales Franchise LLC does not guarantee uninterrupted or error-free service. We make reasonable efforts to maintain availability and security. To the extent permitted by law, we accept no liability for indirect losses, lost profits or incidental damages arising from use or inability to use the platform."
      },
      {
        h: "8. Suspension and termination",
        p: "Canales Franchise LLC may suspend or terminate your access if: (a) you breach these terms; (b) your employment with the company ends; (c) we detect fraudulent or abusive use. After termination only records required by the company for compliance reasons will be retained."
      },
      {
        h: "9. Changes to terms",
        p: "We may update these terms periodically. Changes take effect upon publication. If changes are substantial, we will notify you by email or in-platform. Continued use after the update implies acceptance."
      },
      {
        h: "10. Governing law and contact",
        p: "These terms are governed by the laws of the United States of America. Any dispute shall be submitted to the competent courts at Canales Franchise LLC's headquarters in the United States. For questions write to: antonio@canalesfranchise.com."
      },
    ],
  },
};

export default function TermsConditions() {
  const [locale, setLocale] = useState("en");
  const c = CONTENT[locale];

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Canales Franchise LLC
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-[#6B7280] mb-3">{c.eyebrow}</p>
        <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] mb-3">{c.title}</h1>
        <p className="text-xs text-[#6B7280] mb-8">{c.updated}</p>
        <p className="text-[#374151] leading-relaxed mb-10">{c.intro}</p>

        <div className="space-y-8">
          {c.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading text-xl text-[#1A1A1A] mb-2">{s.h}</h2>
              <p className="text-[#374151] leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <footer className="mt-16 pt-8 border-t border-[#E5E7EB] text-xs text-[#6B7280] flex items-center justify-between flex-wrap gap-2">
          <p>© 2026 Canales Franchise LLC · {c.title}</p>
          <Link to="/privacy" className="text-[#1B2E7A] hover:underline" data-testid="link-privacy">
            {locale === "es" ? "Aviso de Privacidad" : "Privacy Policy"}
          </Link>
        </footer>
      </main>
    </div>
  );
}
